/**
 * `/content` — Relay Content module post list with status tabs.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePostsList } from "@/hooks/use-content";
import type { ContentPostResponse } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const PAGE_LIMIT = 50;

const PLATFORM_EMOJI: Record<ContentPostResponse["platform"], string> = {
  instagram: "📸",
  facebook: "👤",
  linkedin: "💼",
  twitter: "𝕏",
  blog: "📝",
};

const STATUS_STYLES: Record<ContentPostResponse["status"], string> = {
  draft: "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  publishing: "bg-yellow-100 text-yellow-700",
  published: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

type Tab = "all" | ContentPostResponse["status"];

export function ContentHomePage() {
  const [tab, setTab] = useState<Tab>("all");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<ContentPostResponse[]>([]);
  const isLoadMoreRef = useRef(false);
  const navigate = useNavigate();

  const posts = usePostsList({
    limit: PAGE_LIMIT,
    ...(cursor !== undefined && { cursor }),
    ...(tab !== "all" && { status: tab }),
  });

  useEffect(() => {
    if (!posts.data) return;
    if (isLoadMoreRef.current) {
      setAllItems((prev) => [...prev, ...(posts.data?.items ?? [])]);
      isLoadMoreRef.current = false;
    } else {
      setAllItems(posts.data.items);
    }
  }, [posts.data]);

  function handleTabChange(v: Tab) {
    setTab(v);
    setCursor(undefined);
    setAllItems([]);
    isLoadMoreRef.current = false;
  }

  function handleLoadMore() {
    if (!posts.data?.next_cursor) return;
    isLoadMoreRef.current = true;
    setCursor(posts.data.next_cursor);
  }

  const isInitialLoading = posts.isLoading && allItems.length === 0;
  const isFetchingMore = posts.isFetching && allItems.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Module</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Content</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            AI-generated social posts, scheduling, and platform publishing.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/content/analytics">
            <Button variant="outline" size="sm">Analytics</Button>
          </Link>
          <Link to="/content/settings">
            <Button variant="outline" size="sm">Settings</Button>
          </Link>
          <Link to="/content/posts/new">
            <Button>New post</Button>
          </Link>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => {
        if (v === "analytics") {
          navigate("/content/analytics");
          return;
        }
        handleTabChange(v as Tab);
      }}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="draft">Draft</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
          <TabsTrigger value="failed">Failed</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>
      </Tabs>

      {isInitialLoading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="posts-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      )}

      {posts.isError && (
        <p className="text-sm text-destructive">Failed to load posts. Please try again.</p>
      )}

      {!isInitialLoading && allItems.length === 0 && !posts.isError && (
        <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {tab === "all" ? "No posts yet." : `No ${tab} posts.`}
          </p>
          <Link to="/content/posts/new">
            <Button variant="outline" size="sm">Create first post</Button>
          </Link>
        </div>
      )}

      {allItems.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="posts-grid">
            {allItems.map((post) => (
              <Link key={post.id} to={`/content/posts/${post.id}`}>
                <Card className="h-full hover:shadow-md transition-shadow cursor-pointer border-gray-200">
                  <CardContent className="p-4 flex flex-col gap-2 h-full">
                    <div className="flex items-center justify-between">
                      <span className="text-lg">{PLATFORM_EMOJI[post.platform]}</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[post.status]}`}>
                        {post.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-3 flex-1">{post.body}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-2 border-t border-gray-100">
                      <span>{post.platform}</span>
                      <span>
                        {post.publish_at
                          ? `Scheduled ${formatTimestamp(post.publish_at)}`
                          : formatTimestamp(post.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {posts.data?.next_cursor && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isFetchingMore}
                data-testid="posts-load-more"
              >
                {isFetchingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContentHomePage;
